import { FunctionComponent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAccount, useConnect, useDisconnect, useSignMessage } from 'wagmi';

const textColor = '#FFFFFF';
const outlineColor = '#512CBB';
const comicSans = '"Comic Sans", "Comic Sans MS", "Chalkboard", "ChalkboardSE-Regular", sans-serif';

const ANIMA_API = 'https://anima.supply';
const DISCORD_APP_ID = '940349989404684349';
const REDIRECT_URI = 'https://only.bot/discord/connect';

function constructOnlybotsDiscordSignedMessage(date: string, uuid: string): string {
    return `Sign this message to prove ownership of a wallet that holds an Onlybot for the purpose of joining the Anima Discord with the Onlybots role.\n\ndate: ${date}\nuuid: ${uuid}`;
}

function buildDiscordUrl(date: string, uuid: string, address: string, signature: string): string {
    const state = `${date}|${uuid}|${address}|${signature}`;
    let url = 'https://discord.com/api/oauth2/authorize?response_type=code';
    url += `&client_id=${encodeURIComponent(DISCORD_APP_ID)}`;
    url += `&scope=${encodeURIComponent('identify guilds.join')}`;
    url += `&state=${encodeURIComponent(state)}`;
    url += `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
    url += `&prompt=consent`;
    return url;
}

export const Discord: FunctionComponent = () => {
    const { connectors, connectAsync } = useConnect();
    const { disconnectAsync } = useDisconnect();
    const { signMessageAsync } = useSignMessage();
    const { isConnected, address } = useAccount();

    const date = useRef(Date.now().toString());
    const [url, setUrl] = useState<string>('');
    const [phase, setPhase] = useState<
        'INIT' | 'CONNECT' | 'OPEN' | 'SIGN' | 'VERIFYING' | 'DISCORD' | 'NONE' | 'ERROR' | 'COMPLETE'
    >('INIT');

    useLayoutEffect(() => {
        document.body.style.background = '#6A58E7';
        return () => {
            document.body.style.background = '';
        };
    }, []);

    const callback = useCallback(async (code: string, state: string) => {
        try {
            setPhase('VERIFYING');
            const res = await fetch(`${ANIMA_API}/api/v1/discord/onlybots/verify`, {
                credentials: 'include',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code,
                    state,
                    redirectUri: REDIRECT_URI,
                }),
            });

            if (!res.ok) {
                setPhase('ERROR');
                console.error('Failed to verify:', res.status, await res.text());
                return;
            }

            setPhase('COMPLETE');
        } catch (e) {
            setPhase('ERROR');
            console.error('Failed in callback:', e);
        }
    }, []);

    useLayoutEffect(() => {
        const search = new URLSearchParams(window.location.search);
        window.history.replaceState({}, document.title, window.location.pathname);

        const code = search.get('code');
        const state = search.get('state');
        if (code !== null && state !== null) {
            void callback(code, state);
        }
    }, [callback]);

    useLayoutEffect(() => {
        if (phase === 'CONNECT' && isConnected) {
            setPhase('OPEN');
        }
    }, [phase, isConnected]);

    const verify = async (): Promise<void> => {
        if (phase !== 'OPEN' || address === undefined) {
            return;
        }

        try {
            // TODO: wallet signature

            setPhase('VERIFYING');
            const pingRes = await fetch(`${ANIMA_API}/api/v1/discord/onlybots/ping`, {
                credentials: 'include',
                method: 'POST',
            });

            if (!pingRes.ok) {
                setPhase('ERROR');
                console.error('Failed to ping:', pingRes.status, await pingRes.text());
                return;
            }

            const checkRes = await fetch(`${ANIMA_API}/api/v1/discord/onlybots/check`, {
                credentials: 'include',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ address }),
            });

            if (!checkRes.ok) {
                setPhase('ERROR');
                console.error('Failed to check:', checkRes.status, await checkRes.text());
                return;
            }

            const { valid, uuid } = await checkRes.json();

            if (!valid) {
                setPhase('NONE');
                return;
            }

            setPhase('SIGN');
            const message = await signMessageAsync({
                message: constructOnlybotsDiscordSignedMessage(date.current, uuid),
            });
            setUrl(buildDiscordUrl(date.current, uuid, address, message));
            setPhase('DISCORD');
        } catch (e) {
            setPhase('ERROR');
            console.error('Failed to verify:', e);
        }
    };

    return (
        <div
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                paddingY: '8vh',
                paddingX: '30px',
                color: textColor,
                '--webkit-font-smoothing': 'antialiased',
                overflowX: 'hidden',
            }}
        >
            <h1
                sx={{
                    marginTop: 0,
                    fontSize: '48px',
                    fontFamily: comicSans,
                    textShadow: `-3px -3px 0 ${outlineColor}, 0 -3px 0 ${outlineColor}, 3px -3px 0 ${outlineColor}, 3px 0 0 ${outlineColor}, 3px 3px 0 ${outlineColor}, 0 3px 0 ${outlineColor}, -3px 3px 0 ${outlineColor}, -3px 0 0 ${outlineColor}`,
                }}
            >
                Welcome!
            </h1>
            <img
                src="/assets/img/home/discord-guy.webp"
                alt="discord guy"
                sx={{
                    maxWidth: '400px',
                    transform: 'translateX(10%)',
                    marginTop: '-50px',
                    marginBottom: '-50px',
                }}
            />
            <p sx={{ margin: 0, fontSize: '28px', fontFamily: 'serif', maxWidth: '500px', textAlign: 'center' }}>
                Connect a wallet with an{' '}
                <b>
                    <span sx={{ textDecoration: 'underline', fontFamily: comicSans }}>Onlybot</span>
                </b>{' '}
                in it to join the{' '}
                <b>
                    <span sx={{ fontStyle: 'italic' }}>Onlybots Discord BBS</span>
                </b>
            </p>
            <div
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '32px',
                    maxWidth: '500px',

                    '& button': {
                        background: '#5FC0BA',
                        fontSize: '32px',
                        paddingY: '12px',
                        paddingX: '24px',
                        color: textColor,
                        borderRadius: '12px',
                        fontWeight: 900,
                        textShadow: '1px 1px 0 #000',
                    },

                    '& span': {
                        fontFamily: 'sans-serif',
                        fontStyle: 'italic',
                        color: '#0fffff',
                    },
                }}
            >
                {phase === 'INIT' && (
                    <button
                        onClick={() => {
                            if (isConnected) {
                                setPhase('OPEN');
                            } else {
                                setPhase('CONNECT');
                            }
                        }}
                    >
                        Connect
                    </button>
                )}
                {phase === 'CONNECT' &&
                    connectors.map((connector) => (
                        <button
                            key={connector.id}
                            onClick={() =>
                                void (async () => {
                                    try {
                                        await disconnectAsync();
                                    } catch (e) {
                                        console.error(e);
                                    }
                                    await connectAsync({ connector });
                                })()
                            }
                        >
                            {connector.name === 'Injected' ? 'Current Wallet' : connector.name}
                        </button>
                    ))}
                {phase === 'OPEN' && (
                    <button onClick={() => void verify()}>Verify {address?.slice(0, 6) ?? ''}...</button>
                )}
                {phase === 'SIGN' && <span>Complete the signature in your wallet to continue...</span>}
                {phase === 'VERIFYING' && <span>Verifying...</span>}
                {phase === 'NONE' && <span>Sorry, you don't have an Onlybot in that wallet</span>}
                {phase === 'ERROR' && <span>Something went wrong :(</span>}
                {phase === 'DISCORD' && (
                    <button
                        onClick={() => {
                            window.location.href = url;
                        }}
                    >
                        Authorize Discord
                    </button>
                )}
                {phase === 'COMPLETE' && <span>You're in! Check discord.</span>}
            </div>
        </div>
    );
};
